import type React from "react";
import { Box, Typography, Avatar, Link } from "@mui/material";
import { Place as PlaceIcon } from "@mui/icons-material";
import { motion } from "framer-motion";
import QrCode from "react-qr-code";
import type { IExpandedCallSheetData } from "../../../../types/callsheet";
import type { IThemeState } from "../../../../types/theme";
import {
  getCallSheetDisplayName,
  getCallSheetLocation,
} from "./callSheetDisplayName";

interface CallSheetMainInfoProps {
  callSheetData: IExpandedCallSheetData;
  reduxTheme: IThemeState;
}

const CallSheetMainInfo: React.FC<CallSheetMainInfoProps> = ({
  callSheetData,
  reduxTheme,
}) => {
  const displayName = getCallSheetDisplayName(callSheetData);

  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ delay: 0.4, type: "spring", stiffness: 100 }}
      style={{ textAlign: "center" }}
    >
      <Avatar
        src={callSheetData?.booking?.deceased?.[0]?.person_image || ""}
        sx={{
          width: 80,
          height: 80,
          fontSize: "2rem",
          mb: 2,
          alignItems: "center",
          ml: 13,
        }}
      >
        {!callSheetData?.booking?.deceased?.[0]?.person_image && displayName
          ? displayName.charAt(0).toUpperCase()
          : null}
      </Avatar>

      <Typography
        variant="h4"
        sx={{
          mb: 2,
          textAlign: "center",
          fontWeight: "bold",
        }}
      >
        {displayName}
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 2,
        }}
      >
        <PlaceIcon sx={{ mr: 1, color: reduxTheme.primary_colors[400] }} />
        <Typography>{getCallSheetLocation(callSheetData)}</Typography>
      </Box>

      {callSheetData?.website_stream_link && (
        <Box sx={{ mt: 4, textAlign: "center" }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Scan Me
          </Typography>
          <motion.div
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <QrCode
              size={80}
              value={callSheetData?.website_stream_link || "N/A"}
            />
          </motion.div>
          <Typography
            sx={{
              mt: 2,
              fontSize: "12px",
              color: reduxTheme.primary_colors[800],
            }}
          >
            <Link
              href={callSheetData?.website_stream_link || "N/A"}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ wordBreak: "break-word" }}
            >
              {callSheetData?.website_stream_link || "N/A"}
            </Link>
          </Typography>
        </Box>
      )}
    </motion.div>
  );
};

export default CallSheetMainInfo;
